<script lang="ts" setup>
import type {MenuItemRoute} from "~~/route/interface/menu-item-route.interface";
import {menuMainItems} from "~~/route/route.config";
import {EnvelopeOpenIcon, PhoneIcon, MapPinIcon, ArrowRightIcon} from "@heroicons/vue/20/solid";
import Shape from "~/components/icon/Shape.vue";

const {collectionUri, pageUri} = useI18nCollection();
const {locale, t} = useI18n();
const config = useRuntimeConfig().public
const {data: contact} = await useAsyncData(
        () => `contact-${locale.value}`,
        () => queryCollection(collectionUri('home')).path(pageUri('home/contact')).first(),
        {
            watch: [locale]
        }
);
const navItems: MenuItemRoute[] = menuMainItems.filter(item => !item.secondary);
const currentYear = new Date().getFullYear();
const siteName = config.siteName;
const contactInfo = config.contact;
const socials: Record<string, string | { url: string, human: string }> = config.socials;
const privacyPolicyUri = computed(() =>
        pageUri(menuMainItems.find(item => item.identifier === 'privacy-policy')!.path[locale.value])
);
</script>

<template>
  <footer class="p-2 text-light bg-light">
    <div class="bg-secondary pt-16 rounded-img relative z-1">
      <div class="page-container tablet:flex flex-wrap items-stretch justify-between gap-6 laptop:gap-0">
        <div class="mb-4 tablet:w-full laptop:w-1/3 laptop:pe-6">
          <h4 class="section-title text-brand mb-6">
            {{ contact.title }}
          </h4>
          <ContentRenderer :value="contact" class="mb-8 text-2xl font-medium"/>
          <ButtonCta
                  v-if="contact?.meta?.cta"
                  :cta="contact.meta.cta"
                  class="btn-border-green"
          >
            <template #icon-end>
              <ArrowRightIcon class="size-10 ms-2"/>
            </template>
          </ButtonCta>
        </div>
        <div class="tablet:w-full laptop:w-2/3 tablet:flex tablet:justify-between table:items-stretch gap-6">
          <ul class="bg-secondary-dark px-6 py-12 rounded-img mb-10 tablet:w-1/2 h-full tablet:max-w-[280px] laptop:ms-[5vw]">
            <li
                    v-for="item in navItems"
                    :key="item.identifier"
                    :class="{'mb-6': item.identifier === 'projects'}"
                    class="mb-2"
            >
              <SiteHydrationSafeLink
                      :to="pageUri(item.path[locale])"
                      class="transition-colors font-medium text-white hover:text-light text-lg block"
              >
                {{ t(`nav.${item.identifier}`) }}
              </SiteHydrationSafeLink>
            </li>
          </ul>

          <ul class="bg-secondary-alt py-12 px-6 rounded-img tablet:w-1/2 h-full">
            <li v-for="[key, uri] of Object.entries(contactInfo)" :key="key" class="flex items-start gap-6 mb-6">
              <div class="w-12">
                <EnvelopeOpenIcon v-if="key === 'email'" class="size-8 text-brand"/>
                <PhoneIcon v-if="key === 'phone'" class="size-8 text-brand"/>
                <MapPinIcon v-if="key === 'address'" class="size-8 text-brand"/>
              </div>
              <div>
                <span class="block text-white font-medium text-lg">{{ t(key) }}</span>

                <a v-if="key === 'email'" :href="`mailto:${uri}`" class="text-brand-light hover:text-white">
                  {{ uri }}
                </a>
                <a v-else-if="key === 'phone'" :href="`tel:${uri}`" class="text-brand-light hover:text-white">
                  {{ uri }}
                </a>
                <a
                        v-else-if="key === 'address'" :href="uri.url"
                        class="text-brand-light hover:text-white">
                  {{ uri.human }}
                </a>

                <span v-else>
                  {{ uri }}
                </span>
              </div>
            </li>
          </ul>
        </div>
        <div class="border-t border-brand mt-12 py-10 text-sm w-full tablet:flex tablet:items-center tablet:justify-between gap-6">
          <div class="flex gap-4 items-center mb-4 tablet:mb-0">
            <span>&copy; {{ currentYear }} {{ siteName }}</span>
            <a
                    v-for="[name, url] of Object.entries(socials)"
                    :key="name"
                    :href="url" class="text-brand-light">
              <span class="sr-only">{{ name }}</span>
              <IconInstagram v-if="name === 'instagram'" class="size-6"/>
              <IconLinkedin v-if="name === 'linkedin'" class="size-6"/>
            </a>
          </div>
          <div>
            <SiteHydrationSafeLink
                    :to="privacyPolicyUri" class="text-brand-light hover:text-white me-4"
                    rel="noopener"
            >
              {{ t('privacy_policy') }}
            </SiteHydrationSafeLink>
            <a :href="config.terms[locale]" class="text-brand-light hover:text-white">
              {{ t('general_sale_conditions') }}
              <IconExternal class="size-2 align-super"/>
            </a>
          </div>
        </div>
      </div>
      <Shape class="absolute inset-0 m-auto -z-1 opacity-5"/>
    </div>
  </footer>
</template>
